import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { ChevronLeft, Search, Award, Book, MessageSquare, X, Edit2, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Student, Formateur, Class, Appreciation } from '../../types/supabase';

type StudentWithStats = Student & {
  gradesCount: number;
  averageGrade: number;
  class?: {
    name: string;
    class_number: number;
    period: string;
  };
};

const TrainerStudents = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState<StudentWithStats[]>([]);
  const [formateur, setFormateur] = useState<Formateur | null>(null);
  const [classes, setClasses] = useState<Class[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [showAppreciationModal, setShowAppreciationModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentWithStats | null>(null);
  const [showAppreciationForm, setShowAppreciationForm] = useState(false);
  const [appreciationForm, setAppreciationForm] = useState({
    content: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0], 
    subject_id: ''
  });
  const [subjects, setSubjects] = useState<any[]>([]);
  const [appreciations, setAppreciations] = useState<Appreciation[]>([]);
  const [selectedAppreciation, setSelectedAppreciation] = useState<Appreciation | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (user) {
      fetchFormateur();
      fetchClasses();
      fetchSubjects();
      fetchAppreciations();
    }
  }, [user]);

  useEffect(() => {
    if (formateur) {
      fetchStudents();
    }
  }, [formateur, selectedClass]);

  const fetchFormateur = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('formateurs')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching formateur:', error);
        return;
      }

      setFormateur(data);
    } catch (error) {
      console.error('Error fetching formateur:', error);
    }
  };

  const fetchClasses = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('classes')
        .select('*')
        .eq('archived', false)
        .order('name')
        .order('class_number');

      if (error) throw error;
      setClasses(data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchAppreciations = async () => {
    try {
      const { data, error } = await supabase
        .from('appreciations')
        .select(`
          *,
          subject:subjects(name),
          author:user_roles(nom, prenom)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAppreciations(data);
    } catch (error) {
      console.error('Error fetching appreciations:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const { data, error } = await supabase
        .from('subject_trainers')
        .select(`
          subject:subjects (
            id,
            name,
            description,
            coefficient,
            teaching_type,
            class:classes (
              id,
              name,
              class_number,
              period
            )
          )
        `)
        .eq('trainer_id', user?.id)
        .order('subject(name)');

      if (error) throw error;
      // Transform the data to match the expected format
      const transformedSubjects = data.map(item => item.subject);
      setSubjects(transformedSubjects);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setError('Erreur lors du chargement des matières');
    }
  };

  const handleAddAppreciation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent || !user) return;
    try {
      if (!appreciationForm.subject_id) {
        setError('Veuillez sélectionner une matière');
        return;
      }
      const { error } = await supabase
        .from('appreciations')
        .insert([{
          student_id: selectedStudent.id,
          class_id: selectedStudent.class_id,
          subject_id: appreciationForm.subject_id,
          author_id: user.id,
          content: appreciationForm.content,
          start_date: appreciationForm.start_date,
          end_date: appreciationForm.end_date
        }]);

      if (error) throw error;

      // Reset form and refresh data
      setAppreciationForm({
        content: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        subject_id: ''
      });
      setShowAppreciationForm(false);
      await fetchAppreciations();
    } catch (error) {
      console.error('Error adding appreciation:', error);
      setError('Error adding appreciation');
    }
  };

  const handleEditAppreciation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAppreciation || !user) return;

    try {
      if (!appreciationForm.subject_id) {
        setError('Veuillez sélectionner une matière');
        return;
      }

      const { error } = await supabase
        .from('appreciations')
        .update({
          subject_id: appreciationForm.subject_id,
          content: appreciationForm.content,
          start_date: appreciationForm.start_date,
          end_date: appreciationForm.end_date
        })
        .eq('id', selectedAppreciation.id);

      if (error) throw error;

      // Reset form and refresh data
      setAppreciationForm({
        content: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        subject_id: ''
      });
      setIsEditing(false);
      setSelectedAppreciation(null);
      setShowAppreciationForm(false);
      await fetchAppreciations();
    } catch (error) {
      console.error('Error updating appreciation:', error);
      setError('Error updating appreciation');
    }
  };

  const handleDeleteAppreciation = async (id: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette appréciation ?')) return;

    try {
      const { error } = await supabase
        .from('appreciations')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchAppreciations();
    } catch (error) {
      console.error('Error deleting appreciation:', error);
      setError('Error deleting appreciation');
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const fetchStudents = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    try {
      // Get all students from classes where the trainer teaches
      let query = supabase
        .from('students')
        .select(`
          *,
          class:classes(id, name, class_number, period)
        `)
        .order('last_name');

      if (selectedClass) {
        query = query.eq('class_id', selectedClass);
      }

      const { data: studentsData, error: studentsError } = await query;

      if (studentsError) {
        throw studentsError;
      }

      // Get grades for this trainer
      const { data: grades, error: gradesError } = await supabase
        .from('grades')
        .select('*')
        .eq('trainer_id', user.id);

      if (gradesError) {
        throw gradesError;
      }

      // Calculate statistics for each student
      const studentsWithStats = studentsData.map((student) => {
        const studentGrades = grades?.filter(g => g.student_id === student.id) || [];
        const gradesCount = studentGrades.length;
        
        let averageGrade = 0;
        if (gradesCount > 0) {
          const sum = studentGrades.reduce((acc, curr) => {
            if (!curr.is_absent && curr.grade !== null) {
              return acc + (curr.grade * (curr.coefficient || 1));
            }
            return acc;
          }, 0);
          const totalCoef = studentGrades.reduce((acc, curr) => {
            if (!curr.is_absent && curr.grade !== null) {
              return acc + (curr.coefficient || 1);
            }
            return acc;
          }, 0);
          averageGrade = totalCoef > 0 ? Math.round((sum / totalCoef) * 10) / 10 : 0;
        }

        return {
          ...student,
          gradesCount,
          averageGrade,
        };
      });

      setStudents(studentsWithStats);
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Erreur lors du chargement des étudiants');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredStudents = students.filter(student => {
    const fullName = `${student.first_name} ${student.last_name}`.toLowerCase();
    const email = student.email.toLowerCase();
    const term = searchTerm.toLowerCase();
    
    return fullName.includes(term) || email.includes(term);
  });

  // Format class display name
  const formatClassDisplay = (classObj: any) => {
    if (!classObj) return 'Aucune classe';
    return `${classObj.name} ${classObj.class_number} (${classObj.period})`;
  };

  return (
    <div className="animate-fade-in">
      <header className="mb-8">
        <div>
          <Link to="/trainer" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-2">
            <ChevronLeft className="h-4 w-4 mr-1" />
            <span>Retour au tableau de bord</span>
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Mes étudiants</h1>
          <p className="mt-2 text-gray-600">
            Gérez les étudiants de vos classes
          </p>
        </div>
      </header>

      {formateur && (
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <h2 className="text-lg font-semibold">Formateur</h2>
          <p className="text-gray-600">
            {formateur.nom ? `${formateur.prenom || ''} ${formateur.nom}` : formateur.email}
          </p>
        </div>
      )}

      {error && (
        <div className="bg-error-50 text-error-700 p-4 rounded-md mb-6">
          {error}
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative w-full md:w-96">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="input-field pl-10"
            placeholder="Rechercher un étudiant..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="relative w-full md:w-64">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Book className="h-5 w-5 text-gray-400" />
          </div>
          <select
            className="input-field pl-10"
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
          >
            <option value="">Toutes les classes</option>
            {classes.map((classObj) => (
              <option key={classObj.id} value={classObj.id}>
                {formatClassDisplay(classObj)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8">Chargement...</div>
      ) : filteredStudents.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-lg shadow">
          {searchTerm ? 'Aucun résultat trouvé' : 'Vous n\'avez pas encore d\'étudiants'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredStudents.map((student) => (
            <div key={student.id} className="card p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold">
                    {student.first_name} {student.last_name}
                  </h3>
                  <p className="text-gray-600 text-sm">{student.email}</p>
                  <p className="text-gray-500 text-sm mt-1">
                    {student.class ? formatClassDisplay(student.class) : 'Aucune classe'}
                  </p>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  student.averageGrade >= 10 
                    ? 'bg-success-50 text-success-700' 
                    : 'bg-error-50 text-error-700'
                }`}>
                  {student.averageGrade}/20
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                <div className="flex items-center">
                  <Award className="h-5 w-5 text-accent-500 mr-2" />
                  <span className="text-sm text-gray-600">
                    {student.gradesCount} note{student.gradesCount > 1 ? 's' : ''}
                  </span>
                  <span className="mx-2 text-gray-300">|</span>
                  <button
                    onClick={() => {
                      setSelectedStudent(student);
                      setShowAppreciationModal(true);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  >
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Appréciation
                  </button>
                </div>
                <Link 
                  to={`/trainer/grades?student=${student.id}`}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  Voir les notes
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Appreciation Modal */}
      {showAppreciationModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col relative">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold">
                Appréciations de {selectedStudent.first_name} {selectedStudent.last_name}
              </h3>
            </div>
            {/* List of existing appreciations */}
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="space-y-4">
                {appreciations
                  .filter(a => a.student_id === selectedStudent.id)
                  .map(appreciation => (
                    <div key={appreciation.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className="font-medium">{appreciation.subject?.name || 'Appréciation générale'}</span>
                          <p className="text-sm text-gray-500">
                            Du {formatDate(appreciation.start_date)} au {formatDate(appreciation.end_date)}
                          </p>
                        </div>
                        {appreciation.author_id === user?.id && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setSelectedAppreciation(appreciation);
                                setIsEditing(true);
                                setShowAppreciationForm(true);
                                setAppreciationForm({
                                  content: appreciation.content,
                                  start_date: appreciation.start_date,
                                  end_date: appreciation.end_date,
                                  subject_id: appreciation.subject_id || ''
                                });
                              }}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteAppreciation(appreciation.id)}
                              className="text-error-600 hover:text-error-800"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                      <p className="text-gray-700">{appreciation.content}</p>
                      <p className="text-sm text-gray-500 mt-2">
                        Par {appreciation.author?.nom} {appreciation.author?.prenom}
                      </p>
                    </div>
                  ))}
              </div>
            </div>

            {/* Footer with Add button */}
            <div className="p-6 border-t">
              <div className="flex justify-between items-center">
                <button
                  onClick={() => setShowAppreciationModal(false)}
                  className="btn btn-outline"
                >
                  Fermer
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setSelectedAppreciation(null);
                    setShowAppreciationForm(true);
                  }}
                  className="btn btn-primary"
                >
                  Ajouter une appréciation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Appreciation Form Modal */}
      {showAppreciationForm && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">
                {isEditing ? 'Modifier l\'appréciation' : 'Ajouter une appréciation'}
              </h3>

              <form onSubmit={isEditing ? handleEditAppreciation : handleAddAppreciation}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Matière <span className="text-error-500">*</span>
                    </label>
                    <select
                      value={appreciationForm.subject_id}
                      onChange={(e) => setAppreciationForm({
                        ...appreciationForm,
                        subject_id: e.target.value
                      })}
                      className="input-field"
                      required
                    >
                      <option value="">Sélectionner une matière</option>
                      {subjects.map((subject) => (
                        <option key={subject.id} value={subject.id}>
                          {subject.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Période du
                    </label>
                    <input
                      type="date"
                      value={appreciationForm.start_date}
                      onChange={(e) => setAppreciationForm({
                        ...appreciationForm,
                        start_date: e.target.value
                      })}
                      className="input-field"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      au
                    </label>
                    <input
                      type="date"
                      value={appreciationForm.end_date}
                      onChange={(e) => setAppreciationForm({
                        ...appreciationForm,
                        end_date: e.target.value
                      })}
                      className="input-field"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Appréciation
                    </label>
                    <textarea
                      value={appreciationForm.content}
                      onChange={(e) => setAppreciationForm({
                        ...appreciationForm,
                        content: e.target.value
                      })}
                      className="input-field"
                      rows={4}
                      required
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditing(false);
                      setShowAppreciationForm(false);
                      setSelectedAppreciation(null);
                    }}
                    className="btn btn-outline"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    {isEditing ? 'Modifier' : 'Ajouter'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainerStudents;